import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCompany } from '../../../../api/hooks/useCompanies';
import { useAvailableVivaPos } from '../../../../api/hooks/viva/useAvailableVivaPos';
import { getLocalDate } from '../../../../config/utils/dateHelpers';
import { isProviderEnabledForCompany } from '../../../../config/utils/providerUtils';
import { selectSelectedCompany } from '../../../../store/companies/companiesSlice';
import { useAppSelector } from '../../../../store/hooks';
import { CompanyLogo } from './CompanyLogo';
import { SubscriptionSection } from './SubscriptionSection';

export const CompanyDetails = () => {
  const { t } = useTranslation();
  const selectedCompany = useAppSelector(selectSelectedCompany);
  const { data, isSuccess, isLoading } = useCompany(selectedCompany?.id || '');
  const isProviderEnabled = useMemo(() => isProviderEnabledForCompany(selectedCompany), [selectedCompany]);
  useAvailableVivaPos(!isLoading && isProviderEnabled, selectedCompany?.id);

  const getBankDetails = useCallback(() => {
    if (data?.bankDetails?.length) {
      return (
        <div className="margin-t-20 flex-start-start-column-nowrap">
          <div className="padding-b-16 boldText6">{t('SETTINGS.BANKS')}</div>
          <div className="flex-start-center-row-wrap gap-20 border-1-rad-10 padding-10">
            {data?.bankDetails?.map?.((bank, index) => {
              return (
                <div key={index}>
                  <div className="boldText5">{`${t('PDF.BANK_NAME')} - ${t('PDF.SORT_CODE')}`}</div>
                  <div className="normalText5">{`${bank?.name} - ${bank?.swiftCode}`}</div>
                  <div className="normalText5">{bank?.accountHolder}</div>
                  <div className="normalText5">IBAN: {bank?.iban}</div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
  }, [data?.bankDetails, t]);

  const getShippingDetails = useCallback(() => {
    if (data?.shipping?.length) {
      return (
        <div className="margin-t-20 flex-start-start-column-nowrap">
          <div className="padding-b-16 boldText6">{t('SETTINGS.SHIPPING')}</div>
          <div className="flex-start-center-row-wrap gap-20 border-1-rad-10 padding-10">
            {data?.shipping?.map((ship, index) => {
              return (
                <div key={index} className="normalText5">
                  <b>{index + 1}.</b> {ship}
                </div>
              );
            })}
          </div>
        </div>
      );
    }
  }, [data?.shipping, t]);

  const getAadeCredentials = useCallback(() => {
    const aadeUsername = data?.aadeUsername;

    if (aadeUsername) {
      return (
        <>
          <div className="normalText5">
            <b>{t('SETTINGS.AADE_USERNAME')}:</b> {aadeUsername}
          </div>
          <div className="normalText5">
            <b>{t('SETTINGS.AADE_PASSWORD')}:</b> *********
          </div>
        </>
      );
    }
  }, [data?.aadeUsername, t]);

  const getTaxisCredentials = useCallback(() => {
    const taxisUsername = data?.taxisUsername;

    if (taxisUsername) {
      return (
        <>
          <div className="normalText5">
            <b>{t('SETTINGS.TAXIS_USERNAME')}:</b> {taxisUsername}
          </div>
          <div className="normalText5">
            <b>{t('SETTINGS.TAXIS_PASSWORD')}:</b> *********
          </div>
        </>
      );
    }
  }, [data?.taxisUsername, t]);

  const getCompanyTitle = useCallback(() => {
    if ((data?.branchSerial || 0) > 0) return `${data?.title} (${data?.branchSerial})`;

    return data?.title;
  }, [data?.branchSerial, data?.title]);

  const getSection = useCallback(() => {
    if (data && isSuccess) {
      return (
        <div className="flex-center-start-column-nowrap info-card max-width-900">
          <div className="flex-space-between-start-row-nowrap width100 padding-b-16">
            <div>
              <div className="boldText7 padding-b-10">{getCompanyTitle()}</div>
              <div className="normalText5">
                <b>{t('SHARED.VAT_NUMBER')}:</b> {data?.vatNumber}
              </div>
            </div>
            <div>
              <CompanyLogo logoUrl={data?.logoUrl} />
            </div>
          </div>
          <div className="normalText5 padding-b-20">
            <b>{t('SETTINGS.PROFESSION')}:</b> {data?.profession}
          </div>
          <div className="flex-start-center-row-wrap gap-20">
            <div className="normalText5">
              <b>{t('SETTINGS.TOWN')}:</b> {data?.city}
            </div>
            <div className="normalText5">
              <b>{t('SETTINGS.ADDRESS')}:</b> {data?.address}
            </div>
            <div className="normalText5">
              <b>{t('SETTINGS.POSTAL_CODE')}:</b> {data?.postalCode}
            </div>
            <div className="normalText5">
              <b>{t('FORMS.DOY')}:</b> ({data?.doy?.aadeCode}) {data?.doy?.name}
            </div>
            <div className="normalText5">
              <b>{t('FORMS.EMAIL')}:</b> {data?.email}
            </div>
            <div className="normalText5">
              <b>{t('SETTINGS.WEBSITE')}:</b> {data?.websiteUrl}
            </div>
            <div className="normalText5">
              <b>{t('SETTINGS.PHONE_NUMBER')}:</b> {data?.phoneNumber}
            </div>
            {data?.extraInfo?.phone2 && (
              <div className="normalText5">
                <b>{t('SETTINGS.PHONE_NUMBER_2')}:</b> {data.extraInfo.phone2}
              </div>
            )}
            {data?.extraInfo?.phone3 && (
              <div className="normalText5">
                <b>{t('SETTINGS.PHONE_NUMBER_3')}:</b> {data.extraInfo.phone3}
              </div>
            )}
            {data?.extraInfo?.phone4 && (
              <div className="normalText5">
                <b>{t('SETTINGS.PHONE_NUMBER_4')}:</b> {data.extraInfo.phone4}
              </div>
            )}
            <div className="normalText5">
              <b>{t('SETTINGS.GEMI_NUMBER')}:</b> {data?.gemiInfo}
            </div>
            <div className="normalText5">
              <b>{t('SETTINGS.MENO_NUMBER')}:</b> {data?.menoNumber}
            </div>
            <div className="normalText5">
              <b>{t('SETTINGS.EAE_NUMBER')}:</b> {data?.eaeNumber}
            </div>
            <div className="normalText5">
              <b>{t('SHARED.OWNER')}:</b> {data?.owner?.firstName} {data?.owner?.lastName}
            </div>
          </div>
          <div className="flex-start-center-row-wrap gap-20 padding-t-20">{getAadeCredentials()}</div>
          <div className="flex-start-center-row-wrap gap-20 padding-t-20">{getTaxisCredentials()}</div>
          {getBankDetails()}
          {getShippingDetails()}
          <SubscriptionSection />
          <div className="flex-start-center-row-wrap gap-20 padding-t-20">
            <div className="normalText5">
              <b>{t('SETTINGS.CREATED_AT')}:</b> {getLocalDate(data?.createdAt)}
            </div>
            <div className="normalText5">
              <b>{t('SETTINGS.UPDATED_AT')}:</b> {getLocalDate(data?.updatedAt)}
            </div>
          </div>
        </div>
      );
    }

    return <></>;
  }, [
    data,
    isSuccess,
    getCompanyTitle,
    t,
    getAadeCredentials,
    getTaxisCredentials,
    getBankDetails,
    getShippingDetails,
  ]);

  return getSection();
};
